import { AuthService } from '../services/AuthService';
import {
  // type IFetchDeliveryStatusRoot,
  type IFetchTrackingHistoryRoot
} from './../types/IFetchDeliveryStatus';
import {
  // PVZPointV2,
  PVZPointV2Optional,
  // PVZResponseV2,
  PVZResponseV2Optional
} from '@/types/IFetchPvzList';
import {
  type DeliveryOrderData,
  type DeliveryOrderResponse
} from '@/types/DeliveryOrderData';
// TODO(refactor-delivery): deliveryApi (старый сервис) больше не используется —
// импорт убран вместе с переездом на основной бэкенд (см. закомментированный getDeliveryCost).
import { api } from '../utils/api';
import {
  CustomError,
  getErrorData,
  type IGenericResponseRoot
} from '../utils/getErrorData';
import {
  IDeliveryCalculateResponseDataV2,
  IDeliveryCalculateResponseV2
  // TODO(refactor-delivery): типы V1-расчёта — нужны только закомментированной getDeliveryCost.
  // type ICalculateResponse,
  // type IDeliveryCalculateResponse
} from '@/types/IDeliveryCalculateResponse';
import { type ICitiesResponse } from '@/types/ICitiesResponse';
import { type WaybillDownloadResponse } from '@/types/DownloadWayBillResponse';
import { getRequestString } from '../utils/getRequestString';
import {
  type IGetDeliveryAddressRequest,
  type IGetDeliveryAddressResponse
} from '@/types/IGetDeliveryAddress';
import { isAxiosAbortError } from '../utils/typeguards/isAxiosAbortError';
import {
  CreateDeliveryOrderV2Request,
  DeliveryOrderCreateResponse
} from '@/types/DeliveryOrderDataV2';
import {
  ICalculateDeliveryV2Params,
  ICalculateDeliveryV2Response
} from '@/types/ICalculateDeliveryV2';
import { IDownloadLabelResponse } from '@/types/IFailedOrderLabel';
import { DELIVERY_ENDPOINTS } from '../utils/constants/deliveryEndpoints';
import { type IGetByGeoAddressRequest } from '@/types/IGetDeliveryAddress';
// import { getPVZListV2Mock } from '../mocks/pvzMock';

/**
 * Получение полной истории статусов доставки заказа
 * Новый API эндпоинт с массивом всех статусов
 */
export const getOrderTrackingHistory = async (
  orderUuid: string,
  signal?: AbortSignal
): Promise<IFetchTrackingHistoryRoot | 'Abort' | undefined> => {
  try {
    const headers: Record<string, string> = {};
    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
      headers.Accept = 'application/json';
    } else {
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.get<IFetchTrackingHistoryRoot>(
      `/admin/delivery/${orderUuid}/tracking-history`,
      { headers, signal }
    );

    return response.data;
  } catch (error: unknown) {
    if (isAxiosAbortError(error)) {
      return 'Abort';
    }
    // eslint-disable-next-line no-console
    console.error('Ошибка при получении истории статусов заказа:', error);
    return undefined;
  }
};

export const cancelDeliveryOrder = async (orderUuid: string): Promise<void> => {
  try {
    const headers: Record<string, string> = {};
    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
      headers.Accept = 'application/json';
    } else {
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.delete<{ meta: { error: string | null } }>(
      `/admin/delivery/${orderUuid}/cancel`,
      { headers }
    );

    if (response.data.meta?.error != null) {
      throw new Error('Ошибка запроса');
    }
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error('Ошибка при отмене доставки:', error);
    throw error;
  }
};

export const downloadLabel = async (
  orderUuid: string,
  signal?: AbortSignal
): Promise<IDownloadLabelResponse | 'Abort' | undefined> => {
  try {
    const headers: Record<string, string> = {};
    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
      headers.Accept = 'application/json';
    } else {
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.get<{ response: IDownloadLabelResponse }>(
      `/admin/delivery/${orderUuid}/labels`,
      { headers, signal }
    );

    if (response.data?.response === null) {
      throw new Error('Нет информации о ярлыке');
    }

    return response.data.response;
  } catch (error: unknown) {
    if (isAxiosAbortError(error)) {
      return 'Abort';
    }
    // eslint-disable-next-line no-console
    console.error('Ошибка при получении ссылки на файл заказа:', error);
    return undefined;
  }
};

export const downloadWayBill = async (
  orderUuid: string,
  signal?: AbortSignal
): Promise<string | 'Abort' | undefined> => {
  try {
    const headers: Record<string, string> = {};
    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
      headers.Accept = 'application/json';
    } else {
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.get<WaybillDownloadResponse>(
      `/admin/delivery/${orderUuid}/waybills`,
      { headers, signal }
    );

    const waybillItems = response.data?.response?.waybillItems;
    if (
      !Array.isArray(waybillItems) ||
      waybillItems.length === 0 ||
      waybillItems[0].file == null ||
      waybillItems[0].file === ''
    ) {
      // eslint-disable-next-line no-console
      console.error('Нет доступных файлов для скачивания');
      return undefined;
    }

    return waybillItems[0].file;
  } catch (error: unknown) {
    if (isAxiosAbortError(error)) {
      return 'Abort';
    }
    // eslint-disable-next-line no-console
    console.error('Ошибка при получении ссылки на файл заказа:', error);
    return undefined;
  }
};

export const getPVZListV2 = async (
  providerKey: string,
  city: string,
  availableOperation: string,
  signal?: AbortSignal,
  pvzType?: string
): Promise<PVZPointV2Optional[]> => {
  // mock для тестирования
  // if (import.meta.env.MODE === 'development') {
  //   return getPVZListV2Mock(providerKey, city, availableOperation);
  // }

  try {
    const headers: Record<string, string> = {};
    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      throw new Error('Пользователь не авторизирован');
    }

    const queryParams = new URLSearchParams({
      city,
      availableOperations: availableOperation,
      providers: providerKey
    });

    if (pvzType !== undefined && pvzType !== '') {
      queryParams.set('pvz_type', pvzType);
    }

    const url = `${DELIVERY_ENDPOINTS.pvz}?${queryParams.toString()}`;
    const response = await api.get<PVZResponseV2Optional>(url, {
      headers,
      signal
    });

    if (response.data?.meta?.error != null) {
      throw new Error(response.data.meta.error);
    }

    // Собираем все ПВЗ из всех провайдеров в один массив
    const pvzList = Object.values(response.data.response ?? {}).flat();

    return pvzList;
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error('Ошибка при получении списка ПВЗ:', error);
    throw error;
  }
};

export const createDeliveryOrderV2 = async (
  orderUuid: string,
  data: CreateDeliveryOrderV2Request
): Promise<IGenericResponseRoot<DeliveryOrderCreateResponse> | undefined> => {
  try {
    const url = `admin/delivery/${orderUuid}`;
    const res = await api.post<
      IGenericResponseRoot<DeliveryOrderCreateResponse>
    >(url, data);

    if (res?.status !== 200) {
      const { message } = getErrorData(res);
      throw new Error(message);
    }
    return res.data;
  } catch (error: unknown) {
    const { message } = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error('Ошибка при создании заказа на доставку (V2):', message);
  }
};

export const createDeliveryOrder = async (
  data: DeliveryOrderData
): Promise<DeliveryOrderResponse | undefined> => {
  try {
    const requestData: DeliveryOrderData = {
      order: data.order,
      isDeliveryPayedByRecipient: data.isDeliveryPayedByRecipient
    };

    if (
      data.weight !== null &&
      data.weight !== undefined &&
      data.weight !== 0
    ) {
      requestData.weight = data.weight;
    }

    if (
      data.deliveryDate !== null &&
      data.deliveryDate !== undefined &&
      data.deliveryDate !== ''
    ) {
      requestData.deliveryDate = data.deliveryDate;
    }

    if (
      data.comment !== null &&
      data.comment !== undefined &&
      data.comment !== ''
    ) {
      requestData.comment = data.comment;
    }

    if (
      data.date_pickup !== null &&
      data.date_pickup !== undefined &&
      data.date_pickup !== ''
    ) {
      requestData.date_pickup = data.date_pickup;
    }

    if (data.codCost !== null && data.codCost !== undefined) {
      requestData.codCost = data.codCost;
    }

    if (data.deliveryCost !== null && data.deliveryCost !== undefined) {
      requestData.deliveryCost = data.deliveryCost;
    }

    const url = `${import.meta.env.VITE_API_URL}/api/admin/delivery`;

    const res = await api.post<DeliveryOrderResponse>(url, requestData);
    if (res?.status !== 200) {
      const { message } = getErrorData(res);
      throw new Error(message);
    }
    return res.data;
  } catch (error: unknown) {
    const { message } = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error('Ошибка при создании заказа на доставку:', message);
  }
};

export const deleteDeliveryOrder = async (orderUuid: string): Promise<void> => {
  try {
    const headers: Record<string, string> = {};
    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
      headers.Accept = 'application/json';
    } else {
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.delete<{ meta: { error: string | null } }>(
      `/admin/delivery/${orderUuid}/delete`,
      { headers }
    );

    if (response.data.meta?.error != null) {
      throw new Error(response.data.meta.error);
    }
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error('Ошибка при удалении доставки:', error);
    throw error;
  }
};

// TODO(refactor-delivery): V1-расчёт стоимости. Единственная функция, ходившая
// через старый сервис доставки (deliveryApi/VITE_DELIVERY_BACKEND_URL). Не используется
// (потребитель useDeliveryCalculation тоже неактивен — актуальна только V2). Закомментирована
// при отказе от сервиса доставки; оставлена для отката. При восстановлении — перевести на
// инстанс api + DELIVERY_ENDPOINTS.calculate (см. getDeliveryCostV2).
// export const getDeliveryCost = async (
//   city: string,
//   assessedCost: number,
//   providers: string
// ): Promise<ICalculateResponse> => {
//   try {
//     const headers: Record<string, string> = {};
//     if (AuthService.authToken !== null) {
//       headers.Authorization = `Bearer ${AuthService.authToken}`;
//     } else {
//       throw new Error('Пользователь не авторизирован');
//     }
//
//     const queryParams = new URLSearchParams({
//       city,
//       assessedCost: assessedCost.toString(),
//       providers
//     });
//
//     const url = `/api/public/delivery/calculate?${queryParams.toString()}`;
//     const response = await deliveryApi.get<IDeliveryCalculateResponse>(url, {
//       headers
//     });
//
//     if (response.data?.meta?.error != null) {
//       const errorData1 = getErrorData(response.data.meta.error);
//       // eslint-disable-next-line no-console
//       console.error(
//         'Ошибка при получении стоимости доставки:',
//         errorData1.message
//       );
//       throw new Error(errorData1.message);
//     }
//
//     return response.data.response;
//   } catch (error: unknown) {
//     const errorData = getErrorData(error);
//     // eslint-disable-next-line no-console
//     console.error(
//       'Ошибка при получении стоимости доставки:',
//       errorData.message
//     );
//     throw new Error(errorData.message);
//   }
// };

export const getCitiesBySearchValue = async (
  query: string,
  limit: number = 10
): Promise<ICitiesResponse> => {
  try {
    const headers: Record<string, string> = {};
    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      throw new Error('Пользователь не авторизирован');
    }

    const queryParams = new URLSearchParams({
      query,
      limit: limit.toString()
    });

    const url = `${DELIVERY_ENDPOINTS.addressCities}?${queryParams.toString()}`;

    const response = await api.get<ICitiesResponse>(url, { headers });

    return response.data;
  } catch (error: unknown) {
    const errorData = getErrorData(error);
    throw new Error(errorData.message);
  }
};

/**
 * Запрашивает адреса доставки по относительному пути и query-строке,
 * проверяет ответ и возвращает данные. Общая логика для поиска по строке
 * (by-query) и по координатам (by-geo).
 */
const requestDeliveryAddress = async (
  url: string,
  signal?: AbortSignal
): Promise<IGenericResponseRoot<IGetDeliveryAddressResponse>> => {
  const headers: Record<string, string> = {};
  if (AuthService.authToken !== null) {
    headers.Authorization = `Bearer ${AuthService.authToken}`;
  } else {
    throw new Error('Пользователь не авторизирован');
  }

  const response = await api.get<
    IGenericResponseRoot<IGetDeliveryAddressResponse>
  >(url, { headers, signal });

  if (response.status === 500) {
    throw new CustomError(
      'Произошла необработанная ошибка при получении адресов доставки',
      500
    );
  }

  if (response.data?.response == null) {
    throw new CustomError('Не удалось получить адреса доставки');
  }

  return response.data;
};

export const getDeliveryAddress = async (
  options: IGetDeliveryAddressRequest,
  signal?: AbortSignal
): Promise<IGenericResponseRoot<IGetDeliveryAddressResponse>> => {
  try {
    const url = `${DELIVERY_ENDPOINTS.addressesByQuery}${getRequestString(
      options
    )}`;
    return await requestDeliveryAddress(url, signal);
  } catch (error: unknown) {
    const errorData = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error('При получении адресов произошла ошибка:', errorData);
    throw error; // только throw!
  }
};

export const getDeliveryAddressByGeo = async (
  options: IGetByGeoAddressRequest,
  signal?: AbortSignal
): Promise<IGenericResponseRoot<IGetDeliveryAddressResponse>> => {
  try {
    const url = `${DELIVERY_ENDPOINTS.addressesByGeo}${getRequestString(
      options
    )}`;
    return await requestDeliveryAddress(url, signal);
  } catch (error: unknown) {
    const errorData = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error(
      'При получении адресов по координатам произошла ошибка:',
      errorData
    );
    throw error; // только throw!
  }
};

export const getCalculateDeliveryV2 = async ({
  orderUuid,
  providers
}: ICalculateDeliveryV2Params): Promise<
  IGenericResponseRoot<ICalculateDeliveryV2Response>
> => {
  try {
    const headers: Record<string, string> = {};
    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
      headers.Accept = 'application/json';
    } else {
      throw new Error('Пользователь не авторизирован');
    }

    // Бэкенд ожидает providers сырой строкой через запятую (cdek,x5); from_city/to_city
    // больше не принимаются — города определяются по order_uuid.
    const url = `${DELIVERY_ENDPOINTS.adminCalculate(orderUuid)}?providers=${providers}`;

    const response = await api.get<
      IGenericResponseRoot<ICalculateDeliveryV2Response>
    >(url, {
      headers
    });

    if (response.data?.meta?.error != null) {
      const errorData = getErrorData(response.data.meta.error);
      // eslint-disable-next-line no-console
      console.error(
        'Ошибка при расчете стоимости доставки:',
        errorData.message
      );
      throw new Error(errorData.message);
    }

    return response.data;
  } catch (error: unknown) {
    const errorData = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error('Ошибка при расчете стоимости доставки:', errorData.message);
    throw new Error(errorData.message);
  }
};

export const getDeliveryCostV2 = async (
  city: string,
  offers: string,
  providers: string
): Promise<IDeliveryCalculateResponseDataV2> => {
  try {
    const headers: Record<string, string> = {};
    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      throw new Error('Пользователь не авторизирован');
    }

    // Бэкенд ожидает offers/providers сырыми строками с разделителями `=`/`,`
    // (offers=id=count,..., providers=cdek,...). Кодируем только city (кириллица),
    // чтобы не экранировать значимые разделители.
    const queryParams = [
      `city=${encodeURIComponent(city)}`,
      `providers=${providers}`,
      `offers=${offers}`
    ].join('&');

    // TODO(refactor-delivery): ранее URL собирался через VITE_DELIVERY_BACKEND_URL
    // и параметр assessedCost. Переведено на основной бэкенд (инстанс api +
    // DELIVERY_ENDPOINTS.calculate), assessedCost заменён на offers (id=count).
    // const url = `${import.meta.env.VITE_DELIVERY_BACKEND_URL}/api/public/delivery/calculate?...`;
    const url = `${DELIVERY_ENDPOINTS.calculate}?${queryParams}`;

    const response = await api.get<IDeliveryCalculateResponseV2>(url, {
      headers
    });

    if (response.data?.meta?.error != null) {
      const errorData1 = getErrorData(response.data.meta.error);
      // eslint-disable-next-line no-console
      console.error(
        'Ошибка при получении стоимости доставки:',
        errorData1.message
      );
      throw new Error(errorData1.message);
    }

    return response.data.response;
  } catch (error: unknown) {
    const errorData = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error(
      'Ошибка при получении стоимости доставки:',
      errorData.message
    );
    throw new Error(errorData.message);
  }
};
